import * as Yup from 'yup';
import {
  LandChangeDecimalErrorMessage,
  MAX_TWO_DECIMALS_REGEX,
  SoilCarbonDecimalErrorMessage,
  SoilMatterExceedErrorMessage,
  cropResidueSumErrorMessage,
  integerErrorMessage,
  landChangePercentageAtLeastOneErrorMessage,
  landChangePercentageExceedErrorMessage,
  numberErrorMessage,
  percentageAtLeastOneErrorMessage,
  percentageExceedErrorMessage,
  requiredErrorMessage,
  testName,
  totalCropDecimalErrorMessage,
  SoilCarbonExceedErrorMessage,
  SoilMatterDecimalErrorMessage,
  duplicateItemErrorMessage,
} from 'constants/constants';
import { STAGE_4 } from 'constants/FarmboxConstants/stage4.constants';
import { CropResidue, DifferentFields, LandChanges } from 'models/Farmbox/field.model';

interface TestContextExtendedForCrops {
  from: {
    value: DifferentFields;
  }[];
}

interface TestContextExtendedForResidue {
  from: {
    value: CropResidue;
  }[];
}

interface TestContextExtendedForLand {
  from: {
    value: LandChanges;
  }[];
}

const { FIELD_NAMES, HEADING } = STAGE_4;
const {
  TOTAL_CROP_RESIDUE_FIELD,
  RESIDUE_TYPE_FIELD,
  TOTAL_CROP_PERCENTAGE_FIELD,
  SOIL_TEXTURE_FIELD,
  SOIL_MOISTURE_FIELD,
  SOIL_ORGANIC_PERCENTAGE_FIELD,
  SOIL_CARBON_PERCENTAGE_FIELD,
  LAND_LABEL_FIELD,
  LAND_YEAR_CHANGED_FIELD,
  LAND_CHANGED_PERCENTAGE_FIELD,
  COMMENTS_FIELD,
  SOIL_FIELD,
  LAND_MANAGEMENT_FIELD,
  LAND_USE_FIELD,
  CROP_RESIDUE_FIELD,
} = FIELD_NAMES;

const { CROP_SUM, DECIMAL_PRECISION, IS_NUMBER } = testName;

const { COMMODITY } = HEADING;

const fieldRequiredIfAnyOtherFieldFilled = (): Yup.TestFunction =>
  function (this, value) {
    const isEmptyValue = value !== undefined && value !== null && value !== '';

    const {
      [LAND_LABEL_FIELD]: landLabel,
      [LAND_YEAR_CHANGED_FIELD]: landYearChanged,
      [LAND_CHANGED_PERCENTAGE_FIELD]: landChangedPercentage,
    } = this.parent;

    // Check if any of the fields are filled
    const isAnyFieldFilled =
      landLabel || landYearChanged || landChangedPercentage;

    if (isAnyFieldFilled) {
      return isEmptyValue;
    }

    return true;
  };

  const fieldRequiredIfAnyOtherCropsFilled = (): Yup.TestFunction =>
    function (this, value) {
      const isEmptyValue = value !== undefined && value !== null && value !== '';
  
      const {
        [TOTAL_CROP_PERCENTAGE_FIELD]: totalCropResiduePercentage,
        [RESIDUE_TYPE_FIELD]: residueManagementType,
      } = this.parent;
  
      // Check if any of the fields are filled
      const isAnyFieldFilled =
      totalCropResiduePercentage || residueManagementType 
      if (isAnyFieldFilled) {
        return isEmptyValue;
      }
  
      return true;
    };
    const isUniqueLabel = (
      context: Yup.TestContext,
      key: 'landUseChanges' | 'landManagementChanges'
      ) => {
      const array =
      (context as Yup.TestContext & TestContextExtendedForCrops).from[1].value[key] ?? [];
      const currentItem =
      (context as Yup.TestContext & TestContextExtendedForLand).from[0].value ?? {};
      
      if (!currentItem.label) return true;
      const labels = array?.map(item => item.label).filter(Boolean);
      return labels.filter(label => label === currentItem.label).length <= 1;
      };

const CropResidueValidationSchema = Yup.object({
  [TOTAL_CROP_PERCENTAGE_FIELD]: Yup.number().when([RESIDUE_TYPE_FIELD], {
    is: (residueType: string) => !!residueType,
    then: Yup.number()
      .required(requiredErrorMessage)
      .typeError(numberErrorMessage)
      .integer(integerErrorMessage)
      .min(1, percentageAtLeastOneErrorMessage)
      .max(100, percentageExceedErrorMessage)
      .test(CROP_SUM, cropResidueSumErrorMessage, function () {
        const values = (this as Yup.TestContext & TestContextExtendedForCrops)
          .from[1].value.cropResidues;

        if (values?.length === 1) {
          const percentage: number =
            Number(values[0].totalCropResiduePercentage) || 0;
          return percentage === 100;
        }
        const totalCropResidue =
          values?.reduce(
            (sum, item) => sum + Number(item?.totalCropResiduePercentage || 0),
            0
          ) ?? 0;

        return totalCropResidue === 100;
      }),
    otherwise: Yup.number().notRequired(),
  }).test("field-required-if-any-other-crops-filled",requiredErrorMessage,fieldRequiredIfAnyOtherCropsFilled()),
  [RESIDUE_TYPE_FIELD]: Yup.string()
    .typeError(requiredErrorMessage)
    .test('dependent-required-validation', requiredErrorMessage, function () {
      const formValues = (this as Yup.TestContext & TestContextExtendedForCrops)
        .from[1].value;

      const totalCropResidue = Number(formValues?.totalCropResidue) || 0;

      if (totalCropResidue > 0) {
        const residueType = this.parent[RESIDUE_TYPE_FIELD];

        if (!residueType) {
          return false;
        }
      }
      return true;
    })
    .test("field-required-if-any-other-crops-filled",requiredErrorMessage,fieldRequiredIfAnyOtherCropsFilled())
    .test('unique-residentType', duplicateItemErrorMessage, function (this) {
      const context = this as Yup.TestContext & TestContextExtendedForCrops & TestContextExtendedForResidue;
      const array = context.from[1].value.cropResidues ?? [];
      const currentItem = context.from[0].value;
      const residueType = currentItem?.residueManagementType;
    
      if (!residueType) return true;
    
      return array.filter(item => item.residueManagementType === residueType).length <= 1;
    })
});

const SoilValidationSchema = Yup.object({
  [SOIL_TEXTURE_FIELD]: Yup.string()
    .required(requiredErrorMessage)
    .typeError(requiredErrorMessage),
  [SOIL_MOISTURE_FIELD]: Yup.string()
    .required(requiredErrorMessage)
    .typeError(requiredErrorMessage),
  [SOIL_ORGANIC_PERCENTAGE_FIELD]: Yup.number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .required(requiredErrorMessage)
    .typeError(numberErrorMessage)
    .max(100, SoilMatterExceedErrorMessage)
    .test(DECIMAL_PRECISION, SoilMatterDecimalErrorMessage, value => !value || MAX_TWO_DECIMALS_REGEX.test(value.toString())),
  [SOIL_CARBON_PERCENTAGE_FIELD]: Yup.number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .required(requiredErrorMessage)
    .typeError(numberErrorMessage)
    .max(100, SoilCarbonExceedErrorMessage)
    .test(DECIMAL_PRECISION, SoilCarbonDecimalErrorMessage, value => !value || MAX_TWO_DECIMALS_REGEX.test(value.toString())),
});

const LandChangeValidationSchema = Yup.object({
  [LAND_LABEL_FIELD]: Yup.string()
    .nullable()
    .test('requiredIfAnyFieldFilled', requiredErrorMessage,fieldRequiredIfAnyOtherFieldFilled())
    .typeError(requiredErrorMessage)
    .typeError(requiredErrorMessage)
    .test('unique-landUse', duplicateItemErrorMessage, function () {
    return isUniqueLabel(this, 'landUseChanges');
    })
    .test('unique-landManagement', duplicateItemErrorMessage, function () {
    return isUniqueLabel(this, 'landManagementChanges');
    }),
  [LAND_YEAR_CHANGED_FIELD]: Yup.number()
    .nullable()
    .test('requiredIfAnyFieldFilled', requiredErrorMessage,fieldRequiredIfAnyOtherFieldFilled())
    .typeError(requiredErrorMessage),
  from: Yup.string().typeError(requiredErrorMessage).nullable(),
  to: Yup.string().nullable(),
  [LAND_CHANGED_PERCENTAGE_FIELD]: Yup.number()
    .nullable()
    .test('requiredIfAnyFieldFilled', requiredErrorMessage,fieldRequiredIfAnyOtherFieldFilled())
    .typeError(numberErrorMessage)
    .min(1, landChangePercentageAtLeastOneErrorMessage)
    .max(100, landChangePercentageExceedErrorMessage)
    .test(DECIMAL_PRECISION, LandChangeDecimalErrorMessage, value => !value || MAX_TWO_DECIMALS_REGEX.test(value.toString())),
});

export const differentFieldValidationSchema = Yup.object({
  [TOTAL_CROP_RESIDUE_FIELD]: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .test(IS_NUMBER, numberErrorMessage, value => !isNaN(Number(value)))
    .test(DECIMAL_PRECISION, totalCropDecimalErrorMessage, value => !value || MAX_TWO_DECIMALS_REGEX.test(value))
    .optional(),
  [CROP_RESIDUE_FIELD]: Yup.array().of(CropResidueValidationSchema),
  [SOIL_FIELD]: SoilValidationSchema,
  [LAND_USE_FIELD]: Yup.array().of(LandChangeValidationSchema).optional(),
  [LAND_MANAGEMENT_FIELD]: Yup.array()
    .of(LandChangeValidationSchema)
    .optional(),
  [COMMENTS_FIELD]: Yup.string()
    .nullable()
    .typeError(requiredErrorMessage),
  harvestYear: Yup.string().typeError(requiredErrorMessage),
});

export const farmFieldValidationSchema = Yup.object({
  [COMMODITY]: differentFieldValidationSchema,
});
